import { GridFilterModel } from '@mapped/rivet/dist/mui/data-grid'
import { useEffect, useState } from 'react'
import { EStorageKey } from '../../../types/general'
import { withoutSearch } from '../lib/urlState'

export function useDataGridFilter({
  id,
  initial,
  dropStoredSearch,
}: {
  id: string
  /**
   * Wins over the saved model for the first render — the model a shared URL
   * states (`useInitialUrlFilterModel`). It is saved like any other change:
   * this store is the browser tab's working state, not a preference.
   */
  initial?: GridFilterModel
  /**
   * Restore the saved model WITHOUT its search terms — a narrowing link asked
   * for no search here (`isSearchReset`). Ignored when `initial` is given.
   */
  dropStoredSearch?: boolean
}): [GridFilterModel | undefined, (value: GridFilterModel) => void] {
  const storageId = `${EStorageKey.DATA_GRID_FILTER}:${id}`

  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>(
    () => {
      if (initial) return initial
      const stored = getFilterModelFromStorage({ storageId })
      return dropStoredSearch && stored ? withoutSearch(stored) : stored
    }
  )

  useEffect(() => {
    if (filterModel) {
      setFilterModelToStorage({ storageId, filterModel })
    }
  }, [filterModel])

  return [filterModel, setFilterModel]
}

function setFilterModelToStorage({
  storageId,
  filterModel,
}: {
  storageId: string
  filterModel?: GridFilterModel
}) {
  if (!sessionStorage) {
    return
  }

  sessionStorage.setItem(storageId, JSON.stringify(filterModel))
}

function getFilterModelFromStorage({ storageId }: { storageId: string }) {
  if (!sessionStorage) {
    return
  }

  const filterModel = sessionStorage.getItem(storageId)

  if (!filterModel || filterModel === 'undefined') {
    return
  }

  return normalizeFilterModel(JSON.parse(filterModel))
}

function normalizeFilterModel(model: any): GridFilterModel | undefined {
  if (!model || typeof model !== 'object') {
    return
  }

  const normalizedItems = Array.isArray(model.items)
    ? model.items.map((item: any) => normalizeFilterItem(item)).filter(Boolean)
    : []

  return {
    ...model,
    items: normalizedItems,
  }
}

function normalizeFilterItem(item: any) {
  if (!item || typeof item !== 'object') {
    return
  }

  const { columnField, operatorValue, ...rest } = item
  return {
    ...rest,
    field: item.field ?? columnField,
    operator: item.operator ?? operatorValue,
  }
}

/**
 * Forget every grid tab's saved filters in this browser tab.
 *
 * Called when the ORG changes (`switchToOrg`). The store is the tab's working
 * state and is keyed only by grid tab, not by org — so without this, a search
 * typed on Things in one org came back on Things in the next, where it names
 * nothing and opens the grid on "0 rows". A link's own `f`/`search` still
 * apply after the switch: the URL wins over this store anyway.
 */
export function clearSavedGridFilters(): void {
  try {
    const storage = globalThis.sessionStorage
    if (!storage) return
    const prefix = `${EStorageKey.DATA_GRID_FILTER}:`
    const keys: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(prefix)) keys.push(key)
    }
    keys.forEach((key) => storage.removeItem(key))
  } catch {
    // Storage unavailable: there is nothing saved to leak either.
  }
}
